<template>
  <main class="preview-page">
    <CompanyBreadcrumb :aria-label="$t('general.edit_assignment')"
                       :home-aria-label="$t('general.home')"
                       :items="breadcrumbItems"
    />

    <template v-if="errorMessages">
      <div v-for="(error, index) in errorMessages" :key="index" class="uk-alert-danger" uk-alert>
        <a class="uk-alert-close" uk-close></a>
        <p style="color: red">
          <i class="uil-exclamation-triangle"></i> {{ error }}
        </p>
      </div>
    </template>
    <div v-if="successMessage" class="uk-alert-success" uk-alert>
      <a class="uk-alert-close" uk-close></a>
      <p style="color: green">
        <i class="uil-exclamation-triangle"></i> {{ successMessage }}
      </p>
    </div>

    <div class="preview-page-header">
      <h2 class="preview-page-title">{{ $t('general.edit_assignment') }}</h2>
    </div>

    <div class="card preview-card">
      <ul id="main-switcher" class="uk-child-width-expand uk-tab stepper-buttons"
        uk-switcher="connect: #main-switcher-tabs ; animation: uk-animation-slide-left-medium, uk-animation-slide-right-medium">
        <li class="uk-active">
          <a class="p-4" href="#"><i class="uil-invoice"></i> <b> {{ $t('general.overview') }}</b></a>
        </li>
      </ul>

      <default-loading v-if="assignment_loading || !formattedAssignmentItem"></default-loading>
      <template v-else>
        <div v-if="item_loaded" class="card-body preview-body">
          <ul id="main-switcher-tabs" class="uk-switcher uk-margin">
            <li>
              <div class="preview-overview">
                <div class="preview-cover-wrap">
                  <img :src="cover_image_link ? cover_image_link : '@/assets/images/no-photo.png'" alt=""
                    class="preview-cover-img" />
                </div>

                <div class="preview-detail-table-wrap">
                  <table class="preview-detail-table">
                    <tbody>
                      <tr>
                        <td class="preview-detail-label">{{ $t('general.assignment_name') }}</td>
                        <td class="preview-detail-value"><b>{{ name }}</b></td>
                      </tr>
                      <tr>
                        <td class="preview-detail-label">{{ $t('general.number_of_assigned_users') }}:</td>
                        <td class="preview-detail-value"><b>{{ checked_users.length }}</b></td>
                      </tr>
                      <tr>
                        <td class="preview-detail-label">{{ $t('general.give_a_gift_at_the_end') }}:</td>
                        <td class="preview-detail-value">
                          <b>{{ assignment_item.has_present ? $t("general.yes") : $t("general.no") }}</b>
                        </td>
                      </tr>
                      <tr>
                        <td class="preview-detail-label">{{ $t('general.top_rankings_list_display') }}:</td>
                        <td class="preview-detail-value">
                          <b>{{ appear_top ? $t("general.yes") : $t("general.no") }}</b>
                        </td>
                      </tr>
                      <tr>
                        <td class="preview-detail-label">{{ $t('general.start_day') }}:</td>
                        <td class="preview-detail-value"><b>{{ start_date }}</b></td>
                      </tr>
                      <tr>
                        <td class="preview-detail-label">{{ $t('general.final_completion_date') }}</td>
                        <td class="preview-detail-value"><b>{{ end_date }}</b></td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- QR -->
                <div v-if="qrCodeCanBeShown" class="qr-section">
                  <div class="qr-grid">
                    <div v-for="(item, qrIndex) in qr_text_array" :key="qrIndex" class="qr-card">
                      <div class="qr-card-header">
                        <h4 class="qr-card-title">{{ item.title }}</h4>
                      </div>
                      <div class="qr-card-body">
                        <QrCodeStyled :data="item.code" :download-file-name="item.title || 'qr-code'"
                          :download-size="1000" :margin="12" :show-download="true" :size="200"
                          background-color="#ffffff" corners-dot-color="#0c4a6e" corners-dot-type="dot"
                          corners-square-color="#0c4a6e" corners-square-type="extra-rounded" dots-color="#1e3a5f"
                          dots-type="rounded" error-correction-level="H" />
                      </div>
                      <div class="qr-card-info">
                        <div v-if="item.pivot && item.pivot.starts_at" class="qr-date">
                          <i class="uil-calendar-alt"></i>
                          <span>{{ $t('general.start_date') }}: {{ moment(item.pivot.starts_at).format("DD/MM/YYYY HH:mm") }}</span>
                        </div>
                        <div v-if="item.pivot && item.pivot.ends_at" class="qr-date">
                          <i class="uil-clock"></i>
                          <span>{{ $t('general.end_date') }}: {{ moment(item.pivot.ends_at).format("DD/MM/YYYY HH:mm") }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="preview-complete-action">
                  <button class="btn btn-primary preview-complete-btn" type="button" @click="openCompleteModal()">
                    <i class="uil-check-circle"></i> {{ $t('general.complete_assignment') }}
                  </button>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </template>
    </div>

    <!-- Modal -->
    <AppModal :close-on-overlay="false"
              :visible.sync="showCompleteModal"
              :title="$t('general.mark_as_completed')"
              :bottom-sheet="true"
              width="90%"
              max-height="90vh">
      <div class="complete-modal-body-inner">
        <UserGroupSelection :assignment_id="Number($route.params.id)" :checked_users="complete_checked_users"
          :is-groups-active="true" :is-user-list-included="true" :not-assignment-completed="true"
          @setCheckedUsers="($event) => { complete_checked_users = $event }"
          @setCheckedGroups="($event) => { complete_checked_groups = $event }" />
      </div>
      <template #footer>
        <div class="complete-modal-footer">
          <div class="complete-modal-inputs">
            <div class="complete-modal-field">
              <label class="complete-modal-label">{{ $t('general.assignment_complete_date') }}</label>
              <VueCtkDateTimePicker v-model="complete_completed_at" :format="'YYYY-MM-DD HH:mm:ss'"
                                    position="top" />
            </div>
            <div class="complete-modal-field">
              <label class="complete-modal-label">
                {{ $t('general.complete_date_min') }}
                <i :uk-tooltip="'title: ' + $t('general.complete_date_desc') + '; pos: top-center'"
                  class="uil-info-circle ml-1" />
              </label>
              <input v-model.number="complete_duration" class="uk-input complete-modal-input" min="0"
                :placeholder="$t('general.eg_30')" type="number" />
            </div>
          </div>
          <div class="complete-modal-actions">
            <button class="uk-button uk-button-default complete-modal-btn" type="button" @click="showCompleteModal = false">
              <i class="uil-times"></i> {{ $t("general.close") }}
            </button>
            <button class="uk-button uk-button-primary complete-modal-btn" type="button" @click="saveCompleteUsers">
              <i class="uil-check"></i> {{ $t('general.save_and_close') }}
            </button>
          </div>
        </div>
      </template>
    </AppModal>
  </main>
</template>

<script>
import store from "@/core/services";
import UserGroupSelection from "@/view/components/assignment/UserGroupSelection";
import DefaultLoading from "@/view/components/loading/DefaultLoading";
import AppModal from "@/view/components/ui/AppModal";
import CompanyBreadcrumb from "@/view/company/components/breadcrumb/CompanyBreadcrumb.vue";

import assignment_module, {
  BASE_URL as ASSIGNMENT_BASE_URL,
  GET_ITEM as ASSIGNMENT_GET_ITEM,
  ITEM as ASSIGNMENT_ITEM,
  LOADING as ASSIGNMENT_LOADING,
  MODULE_NAME as ASSIGNMENT_MODULE_NAME,
} from "@/core/services/store/assignment.module";

import QrCodeStyled from "@/view/components/ui/QrCodeStyled.vue";
import { createQrCodeForEveryClassroomEducation } from "@/view/company/Assignments/helpers/qrCodeHelper";
import moment from "moment/moment";
import axios from "axios";
import { showError, showSuccess } from "@/utils/alertUtils";
import { convertUTCToLocal } from "@/utils/timeUtils";

const _ASSIGNMENT_MODULE = ASSIGNMENT_MODULE_NAME;

export default {
  name: "AssignmentPreview",
  components: {
    UserGroupSelection,
    DefaultLoading,
    QrCodeStyled,
    AppModal,
    CompanyBreadcrumb,
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(_ASSIGNMENT_MODULE, assignment_module);
  },
  data() {
    return {
      qrCodeCanBeShown: false,
      successMessage: null,
      errorMessages: [],
      cover_image_link: null,
      checked_users: [],
      name: null,
      start_date: null,
      end_date: null,
      appear_top: false,
      item_loaded: false,
      qr_text_array: [],
      complete_checked_users: [],
      complete_checked_groups: [],
      complete_completed_at: null,
      complete_duration: null,
      showCompleteModal: false,
    };
  },
  computed: {
    breadcrumbItems() {
      return [
        { name: this.$t('general.assignments'), route: { name: 'company.assignments.list' } },
        { name: this.$t('general.edit_assignment') },
      ];
    },
    moment() {
      return moment
    },
    assignment_item: {
      get() {
        return store.getters[ASSIGNMENT_MODULE_NAME + "/" + ASSIGNMENT_ITEM];
      },
      set(value) {
      },
    },
    assignment_loading: {
      get() {
        return store.getters[ASSIGNMENT_MODULE_NAME + "/" + ASSIGNMENT_LOADING];
      },
      set(value) {
      },
    },
    formattedAssignmentItem() {
      const item = this.assignment_item;

      this.item_loaded = false;
      if (item == null || item.users == undefined) return false;
      this.checked_users = Array.isArray(item.active_users) ? item.active_users : [];
      this.name = item.name;
      this.start_date = convertUTCToLocal(item.start_date);
      this.end_date = convertUTCToLocal(item.end_date);
      this.appear_top = item.appear_top;
      this.cover_image_link = item.cover_image_link;
      this.item_loaded = true;
      return true;
    },
  },
  methods: {
    openCompleteModal() {
      this.complete_checked_users = [];
      this.complete_checked_groups = [];
      this.complete_completed_at = moment().format("YYYY-MM-DD HH:mm:ss");
      this.complete_duration = null;
      this.showCompleteModal = true;
    },
    saveCompleteUsers() {
      const hasUsers = this.complete_checked_users && this.complete_checked_users.length > 0;
      const groupIds = Array.isArray(this.complete_checked_groups)
        ? this.complete_checked_groups
        : (this.complete_checked_groups?.ids || []);
      const hasGroups = groupIds.length > 0;

      if (!hasUsers && !hasGroups) {
        showError(this.$t('general.please_select_user_or_group'));
        return;
      }

      const assignmentId = this.$route.params.id;
      const completedAt = moment(this.complete_completed_at)
        .utc()
        .format("YYYY-MM-DDTHH:mm:ss[Z]");

      const duration = this.complete_duration !== null && this.complete_duration !== '' ? this.complete_duration : null;

      const payload = {
        user_ids: this.complete_checked_users || [],
        group_ids: groupIds,
        completed_at: completedAt,
        duration: duration
      };

      axios.put(`${ASSIGNMENT_BASE_URL}/bulk-complete/${assignmentId}`, payload)
        .then(() => {
          this.scrollToTop();
          showSuccess(this.$t('general.selected_users_marked_completed'));
          this.showCompleteModal = false;
        })
        .catch((error) => {
          const errorMessage = error.response?.data?.message || error.message || this.$t('general.error_occurred');
          showError(errorMessage);
        });
    },
    getAssignmentItem() {
      this.$store.dispatch(ASSIGNMENT_MODULE_NAME + "/" + ASSIGNMENT_GET_ITEM, {
        url: ASSIGNMENT_BASE_URL + "/" + this.$route.params.id,
      }).then((response) => {
        this.qr_text_array = createQrCodeForEveryClassroomEducation({
          result: response,
          assignmentId: this.$route.params.id
        });
        if (this.qr_text_array?.length) {
          this.qrCodeCanBeShown = true;
        }
      });
    },
    resetMessages() {
      this.successMessage = null;
      this.errorMessages = [];
    },
    scrollToTop() {
      window.scroll({
        top: 0,
        behavior: "smooth",
      });
    },
  },
  mounted() {
    this.getAssignmentItem();
  },
  watch: {
    successMessage() {
      this.debouncedResetValues();
    },
    errorMessages() {
      this.debouncedResetValues();
    },
  },
  created() {
    this.debouncedResetValues = _.debounce(this.resetMessages, 3000);
  },
};
</script>

<style scoped>
/* ── Base ── */
.preview-page {
  --preview-text: #1e293b;
  --preview-muted: #64748b;
  --preview-border: #e2e8f0;
  min-width: 0;
}

/* ── Header ── */
.preview-page-header {
  margin-bottom: 16px;
}

.preview-page-title {
  margin: 0;
  color: var(--preview-text);
  font-size: 19px;
  font-weight: 600;
  line-height: 1.3;
}

/* ── Card ── */
.preview-card {
  border: 1px solid var(--preview-border);
  border-radius: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.preview-body {
  padding: 24px;
}

/* ── Overview layout ── */
.preview-overview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

/* ── Cover image ── */
.preview-cover-wrap {
  width: 100%;
  max-width: 480px;
}

.preview-cover-img {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 12px;
}

/* ── Detail table ── */
.preview-detail-table-wrap {
  width: 100%;
  max-width: 600px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.preview-detail-table {
  width: 100%;
  border-collapse: collapse;
}

.preview-detail-table tr {
  border-bottom: 1px solid var(--preview-border);
}

.preview-detail-table tr:last-child {
  border-bottom: none;
}

.preview-detail-table td {
  padding: 12px 8px;
  vertical-align: middle;
  font-size: 14px;
  line-height: 1.5;
}

.preview-detail-label {
  color: var(--preview-muted);
  font-weight: 500;
  white-space: nowrap;
  text-align: right;
  width: 45%;
}

.preview-detail-value {
  color: var(--preview-text);
  text-align: left;
}

/* ── Complete action ── */
.preview-complete-action {
  width: 100%;
  max-width: 480px;
}

.preview-complete-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.preview-complete-btn:active:not(:disabled) {
  transform: scale(0.98);
  opacity: 0.85;
}

/* ── QR section ── */
.qr-section {
  width: 100%;
  padding-top: 24px;
  border-top: 1px solid var(--preview-border);
}

.qr-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  max-width: 720px;
  margin: 0 auto;
}

.qr-card {
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border: 1px solid var(--preview-border);
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.qr-card-header {
  text-align: center;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--preview-border);
}

.qr-card-title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--preview-text);
  line-height: 1.4;
}

.qr-card-body {
  display: flex;
  justify-content: center;
  padding: 8px 0;
}

.qr-card-info {
  margin: 12px -20px -20px;
  padding: 12px 20px;
  border-top: 1px solid var(--preview-border);
  background: #f8fafc;
  border-radius: 0 0 16px 16px;
}

.qr-date {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--preview-muted);
  margin-bottom: 4px;
}

.qr-date:last-child {
  margin-bottom: 0;
}

.qr-date i {
  color: #0ea5e9;
  font-size: 16px;
}

.qr-date span {
  font-weight: 500;
}

/* ── Modal body ── */
.complete-modal-body-inner {
  width: 100%;
}

/* ── Modal footer ── */
.complete-modal-footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
}

.complete-modal-inputs {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex: 1 1 auto;
  min-width: 0;
}

.complete-modal-field {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 0;
}

.complete-modal-label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 500;
  color: var(--preview-text);
  line-height: 1.4;
}

.complete-modal-field >>> .date-time-picker,
.complete-modal-field >>> .date-time-picker * {
  margin: 0 !important;
}

.complete-modal-field >>> .date-time-picker .datetimepicker {
  z-index: 9999 !important;
}

.complete-modal-field >>> .date-time-picker .field-input {
  height: 42px !important;
  min-height: 42px !important;
  border-radius: 10px !important;
  font-size: 14px !important;
}

.complete-modal-input {
  height: 42px;
  min-height: 42px;
  margin: 0;
  border-radius: 10px;
  font-size: 14px;
}

.complete-modal-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 8px;
}

.complete-modal-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 42px;
  padding: 8px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.complete-modal-btn:active:not(:disabled) {
  transform: scale(0.98);
  opacity: 0.85;
}

/* ── Tablet: 960–1199 ── */
@media (max-width: 1199px) and (min-width: 960px) {
  .preview-body {
    padding: 20px;
  }
}

/* ── Mobile: <960px ── */
@media (max-width: 959px) {
  .preview-page {
    padding: 0 0 24px;
  }

  .preview-page-title {
    font-size: 24px;
    font-weight: 700;
  }

  .preview-body {
    padding: 16px 12px;
  }

  .preview-overview {
    gap: 20px;
  }

  /* Cover */
  .preview-cover-wrap {
    max-width: none;
  }

  .preview-cover-img {
    border-radius: 10px;
  }

  /* Detail table: stack labels and values */
  .preview-detail-table-wrap {
    max-width: none;
  }

  .preview-detail-table td {
    display: block;
    width: 100%;
    text-align: left;
    padding: 4px 0;
  }

  .preview-detail-label {
    text-align: left;
    white-space: normal;
    color: var(--preview-muted);
    font-size: 13px;
    padding-top: 12px;
  }

  .preview-detail-value {
    font-size: 15px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--preview-border);
  }

  .preview-detail-table tr {
    border-bottom: none;
  }

  .preview-detail-table tr:last-child .preview-detail-value {
    border-bottom: none;
    padding-bottom: 4px;
  }

  /* Complete button */
  .preview-complete-action {
    max-width: none;
  }

  .preview-complete-btn {
    min-height: 48px;
    font-size: 16px;
  }

  /* QR */
  .qr-grid {
    grid-template-columns: 1fr;
    max-width: none;
  }

  .qr-card {
    padding: 16px;
  }

  .qr-card-info {
    margin: 12px -16px -16px;
    padding: 12px 16px;
    border-radius: 0 0 16px 16px;
  }

  /* Modal body */
  .complete-modal-body-inner {
    min-height: 0;
  }

  /* Modal footer */
  .complete-modal-footer {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .complete-modal-inputs {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .complete-modal-field {
    width: 100%;
    flex: 1 1 auto;
  }

  .complete-modal-label {
    font-size: 14px;
    margin-bottom: 8px;
  }

  .complete-modal-field >>> .date-time-picker .field-input {
    height: 48px !important;
    min-height: 48px !important;
    font-size: 16px !important;
    border-radius: 10px !important;
  }

  .complete-modal-input {
    height: 48px;
    min-height: 48px;
    font-size: 16px;
    border-radius: 10px;
  }

  .complete-modal-actions {
    flex-direction: column;
    gap: 8px;
  }

  .complete-modal-btn {
    width: 100%;
    min-height: 48px;
    padding: 12px 24px;
    font-size: 16px;
  }
}

/* ── Hover ── */
@media (hover: hover) and (pointer: fine) {
  .preview-complete-btn:hover:not(:disabled) {
    opacity: 0.92;
  }

  .qr-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(14, 165, 233, 0.15);
  }
}

/* ── Reduced motion ── */
@media (prefers-reduced-motion: reduce) {
  .preview-complete-btn:active:not(:disabled) {
    transform: none;
  }

  .qr-card:hover {
    transform: none;
  }
}
</style>
